import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Link,
  CssBaseline,
  ThemeProvider,
  createTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Formik, Form, useField, useFormikContext } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getFcmToken } from "../../utils/token";
import bannerImage from "../../assets/banner.jpeg";
import buyerImage from "../../assets/buy_scrap_online.jpeg";
import sellerImage from "../../assets/sell_scrap_online.jpeg";
import { registerUser } from "../../features/auth/authSlice";
import { showAppMessageToast } from "../../utils/toastUtils";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { COUNTRY_CODES } from "../../utils/countryCodes";

// This file is a self-contained React application that recreates the register page
// using Material-UI components, following the same responsive layout and design
// principles as the login page. All external dependencies are mocked to
// make this a runnable, single-file example.

// MOCKING EXTERNAL DEPENDENCIES FOR A STANDALONE EXAMPLE
// const useNavigate = () => (path) => console.log(`Navigating to: ${path}`);
// const useDispatch = () => () => ({ unwrap: () => Promise.resolve({ data: { email: 'test@example.com' } }) });
// const showAppMessageToast = ({ type, title, message }) => console.log(`${type}: ${title}\n${message}`);
// const getFcmToken = () => "mock-fcm-token";
// const bannerImage = "https://placehold.co/1080x1920/1A237E/fff?text=Register+Page+Banner";
// const registerUser = () => ({ type: "mock/register" });

// Custom theme to match the colors from the login page
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#1976d2",
    },
    secondary: {
      main: "#ff9800",
      contrastText: "#fff",
    },
    scraptradeYellow: {
      main: "#F4C542",
      dark: "#E3AE27",
    },
    scraptradeDark: {
      main: "#2D2D2D",
    },
    background: {
      default: "#f4f6f8",
    },
  },
  typography: {
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "8px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E3AE27",
          },
          // New style to target placeholder text on focus
          "&.Mui-focused .MuiInputBase-input::placeholder": {
            color: "#E3AE27", // Use a dark yellow for better contrast
            opacity: 1, // Ensure it is fully visible
          },
          // New style to target the label on focus
          "&.Mui-focused .MuiInputLabel-root": {
            color: "#E3AE27",
          },
        },
        notchedOutline: {
          borderColor: "transparent",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          "&.Mui-focused": {
            color: "#E3AE27",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: "#f0f4f9",
          borderRadius: "8px",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "transparent",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#F4C542",
          },
        },
      },
    },
  },
});

// A custom Formik-compatible TextField component
const FormikTextField = ({ label, type, ...props }) => {
  const [field, meta] = useField(props);
  const [showPassword, setShowPassword] = useState(false);
  const errorText = meta.error && meta.touched ? meta.error : "";

  const isPassword = type === "password";

  return (
    <TextField
      {...props}
      {...field}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      type={isPassword ? (showPassword ? "text" : "password") : type}
      error={!!errorText}
      helperText={errorText}
      InputProps={{
        endAdornment: isPassword ? (
          <IconButton
            onClick={() => setShowPassword(!showPassword)}
            size="small"
            edge="end"
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        ) : null,
      }}
    />
  );
};

// A custom Formik-compatible Select component for country codes, with role-based filtering
const FormikPhoneInput = ({ label, role, ...props }) => {
  const { setFieldValue } = useFormikContext();
  const [phoneField, phoneMeta] = useField("phone");
  const [codeField, codeMeta] = useField("countryCode");

  const country = COUNTRY_CODES.find((c) => c.code === codeField.value);
  const maxLength = country?.phoneLength;

  React.useEffect(() => {
    if (maxLength && phoneField.value && phoneField.value.length > maxLength) {
      setFieldValue("phone", phoneField.value.slice(0, maxLength));
    }
  }, [codeField.value, maxLength, phoneField.value, setFieldValue]);

  const errorText =
    (phoneMeta.touched && phoneMeta.error) ||
    (codeMeta.touched && codeMeta.error);

  const options =
    role === "seller"
      ? COUNTRY_CODES
      : // ? COUNTRY_CODES.filter((c) => c.code === "+61")
      COUNTRY_CODES;

  return (
    <TextField
      {...phoneField}
      {...props}
      label={label}
      fullWidth
      margin="normal"
      variant="outlined"
      type="tel"
      error={!!errorText}
      helperText={errorText}
      inputProps={{
        maxLength: maxLength,
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%", // 🔑 critical
                pl: 1,
                pr: 0.5,
              }}
            >
              <Select
                displayEmpty
                value={codeField.value}
                onChange={(e) => setFieldValue("countryCode", e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                // disabled={role === "seller"}
                variant="standard"
                disableUnderline
                renderValue={(selected) => selected || "Select"}
                sx={{
                  fontWeight: 600,
                  minWidth: 52,

                  // 🔥 THIS is what actually aligns text
                  "& .MuiSelect-select": {
                    padding: 0,
                    display: "flex",
                    alignItems: "center",
                    height: "100%",
                  },

                  "& .MuiSelect-icon": {
                    right: 0,
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em>Select Code</em>
                </MenuItem>
                {options.map((c) => (
                  <MenuItem key={c.value} value={c.code}>
                    {c.label} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </InputAdornment >
        ),
      }}
    />
  );
};

// A custom Formik-compatible Select component
const FormikSelectField = ({ label, options, ...props }) => {
  const [field, meta] = useField(props);
  const errorText = meta.error && meta.touched ? meta.error : "";
  return (
    <FormControl fullWidth margin="normal" error={!!errorText}>
      <InputLabel>{label}</InputLabel>
      <Select {...props} {...field} label={label} variant="outlined">
        <MenuItem value="">
          <em>Select a role</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
      {errorText && <FormHelperText>{errorText}</FormHelperText>}
    </FormControl>
  );
};

const registerValidationSchema = Yup.object({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  countryCode: Yup.string().required("Country code is required"),
  phone: Yup.string()
    .transform((v) => v?.replace(/\D/g, "")) // remove spaces, dashes
    .required("Phone number is required")
    .test("len", "Invalid phone number length", function (val) {
      const { countryCode } = this.parent;
      if (!countryCode) return true; // Let the countryCode requirement trigger first
      const country = COUNTRY_CODES.find((c) => c.code === countryCode);
      const requiredLen = country?.phoneLength;
      if (!val) return true;
      return val.length === requiredLen;
    }),
  password: Yup.string().min(6, "Password too short").required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm Password is required"),
  role: Yup.string()
    .oneOf(["buyer", "seller"], "Invalid Role")
    .required("Role is required"),
});

const taglines = {
  buyer: "Buy from trusted scrap sellers across the country.",
  seller: "Sell your scrap faster with verified buyers.",
  default: "Connect with verified scrap buyers & sellers.",
};

const Register = () => {
  const isMobile = useMediaQuery(customTheme.breakpoints.down("md"));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedRole = location.state?.role || "buyer";
  const ROLE_CONTENT = {
    buyer: {
      title: "Create Buyer/Seller Account",
      subtitle: "Start sourcing scrap from verified sellers.",
      image: buyerImage,
    },
    seller: {
      title: "Create Seller Account",
      subtitle: "Start selling scrap to verified buyers.",
      note: "Seller onboarding is limited to individuals and businesses operating in Australia.",
      image: sellerImage,
    },
  };

  const roleContent = ROLE_CONTENT[preselectedRole];
  console.log("preselectedRole in register:", preselectedRole);

  const [role, setRole] = useState("");

  const initialValues = {
    name: "",
    email: "",
    countryCode: "",
    phone: "",
    companyName: "",
    password: "",
    confirmPassword: "",
    role: preselectedRole,
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const fcm_token = getFcmToken();
    try {
      const payload = {
        name: values.name,
        email: values.email,
        phone: `${values.countryCode}${values.phone}`,
        companyName: values.company_name,
        password: values.password,
        password_confirmation: values.confirmPassword,
        role: preselectedRole,
        fcm_token,
      };
      const res = await dispatch(registerUser(payload)).unwrap();

      showAppMessageToast({
        type: "success",
        title: "Registration Successful",
        message: "OTP has been sent to your registered phone number.",
      });

      if (res?.data?.email) {
        navigate("/request-otp", {
          state: {
            email: res.data.email,
            role: preselectedRole,
            source: "register",
          },
        });
      }
    } catch (error) {
      showAppMessageToast({
        type: "error",
        title: "Registration Failed",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Something went wrong. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      {/* Left Banner Section - hidden on mobile */}
      {!isMobile && (
        <Grid
          item
          md={8}
          sx={{
            backgroundImage: `url(${roleContent.image})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "500px",
          }}
        >
          {/* Banner Overlay */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.55)",
              zIndex: 1,
            }}
          />
          {/* Banner Text Content */}
          <Box
            sx={{
              zIndex: 2,
              textAlign: "center",
              p: 4,
              minWidth: "786px",
              color: "white",
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              fontWeight={700}
              sx={{ my: 1, fontSize: "2.5rem" }}
            >
              Scrap Trade
            </Typography>
            <Typography variant="body1" sx={{ color: "#ddd" }}>
              {roleContent.subtitle}
            </Typography>
          </Box>
        </Grid>
      )}
      {/* Right Form Section */}
      <Grid
        item
        xs={12}
        md={4}
        component={Paper}
        elevation={0}
        square
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: customTheme.palette.background.default,
          flexGrow: 1,
          p: { xs: 2, md: 4 },
        }}
      >
        <Box
          sx={{
            my: { xs: 4, md: 8 },
            p: 4,
            width: "100%",
            maxWidth: 440,
            borderRadius: "16px",
            boxShadow: "0 0 30px rgba(0,0,0,0.05)",
            backgroundColor: "white",
          }}
        >
          <Typography component="h2" variant="h5" fontWeight={600} mb={3}>
            {roleContent.title}
          </Typography>
          {/* {roleContent.note && (
            <Box
              sx={{
                mt: 2,
                px: 1.5,
                py: 0.8,
                borderRadius: "10px",
                background:
                  "linear-gradient(135deg, rgba(255,193,7,0.12), rgba(255,193,7,0.03))",
                border: "1px solid rgba(255,193,7,0.35)",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#8A5A00",
                  letterSpacing: "0.2px",
                }}
              >
                {roleContent.note}
              </Typography>
            </Box>
          )} */}

          <Formik
            initialValues={initialValues}
            validationSchema={registerValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, isValid, dirty, isSubmitting, setFieldValue }) => (
              <Form noValidate>
                <FormikTextField
                  name="name"
                  label="Name"
                  type="text"
                  placeholder="Enter Name"
                />
                <FormikTextField
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter Email"
                />
                <FormikPhoneInput
                  name="phone"
                  label="Phone Number"
                  role={preselectedRole}
                  placeholder="Enter phone number"
                />
                <FormikTextField
                  name="companyName"
                  label="Company Name"
                  type="text"
                  placeholder="Enter Company Name"
                />
                {/* <FormikTextField
                  name="phone"
                  label="Phone"
                  type="phone"
                  placeholder="Enter Phone"
                /> */}
                <FormikTextField
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter Password"
                />
                <FormikTextField
                  name="confirmPassword"
                  label="Confirm Password"
                  type="password"
                  placeholder="Enter Confirm Password"
                />
                {/* <FormikSelectField
                  name="role"
                  label="Role"
                  options={[
                    { label: "Buyer", value: "buyer" },
                    { label: "Seller", value: "seller" },
                  ]}
                  onChange={(e) => {
                    setFieldValue("role", e.target.value);
                    setRole(e.target.value);
                  }}
                  sx={{
                    // Outline / border + focus overrides
                    "& .MuiOutlinedInput-root": {
                      // default outline
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "transparent",
                      },

                      // hover border
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: customTheme.palette.scraptradeYellow.main,
                      },

                      // MUI focused outline (use !important to beat UA in some cases)
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${customTheme.palette.scraptradeYellow.dark} !important`,
                        borderWidth: "1px",
                      },

                      // Prevent browser/Chrome blue focus ring on the internal select/input
                      "& .MuiSelect-select:focus, & select:focus, & input:focus":
                        {
                          outline: "none",
                          boxShadow: "none",
                          WebkitBoxShadow: "none",
                          WebkitTapHighlightColor: "transparent",
                          backgroundColor: "transparent",
                        },

                      // Optional: subtle custom focus glow instead of the blue ring
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 4px ${customTheme.palette.scraptradeYellow.main}33`, // 33 = ~20% alpha
                        borderRadius: "8px",
                      },
                    },

                    // Floating label color on focus
                    "& label.Mui-focused": {
                      color: `${customTheme.palette.scraptradeYellow.dark} !important`,
                    },
                  }}
                /> */}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    mt: 3,
                    mb: 2,
                    fontWeight: 600,
                    backgroundColor: customTheme.palette.scraptradeYellow.main,
                    color: "#2D3A27",
                    "&:hover": {
                      backgroundColor:
                        customTheme.palette.scraptradeYellow.dark,
                    },
                  }}
                  disabled={!(isValid && dirty) || isSubmitting}
                >
                  Register
                </Button>
                <Grid container justifyContent="center" sx={{ mt: 2 }}>
                  <Grid item>
                    <Typography variant="body2" component="span">
                      Already have an account?{" "}
                    </Typography>
                    <Link
                      component={RouterLink}
                      to="/login"
                      state={{ role: preselectedRole }}
                      variant="body2"
                      sx={{
                        textDecoration: "none",
                        color: customTheme.palette.scraptradeYellow.main,
                        fontWeight: 600,
                      }}
                    >
                      Login
                    </Link>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </Box>
  );
};

// Main App component with theme provider
export default function App() {
  return (
    <ThemeProvider theme={customTheme}>
      <CssBaseline />
      <Register />
    </ThemeProvider>
  );
}
